import { create } from "zustand";
import { setStickyHeader } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";

/*
 * The Plan's Phone view runs this same app in an iframe on a preview session.
 * This frame remembers that it is one (sessionStorage is per frame), sends
 * `X-KK-App: preview` with every request so the server reads the preview
 * cookie, and reloads its data when the Plan posts `kk:refresh`.
 */

const KEY = "kk.staff.preview";

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

type State = { on: boolean; set: (on: boolean) => void };

export const usePreviewModeStore = create<State>((set) => ({
  on: readFlag(),
  set: (on) => {
    try {
      if (on) sessionStorage.setItem(KEY, "1");
      else sessionStorage.removeItem(KEY);
    } catch {
      /* A blocked sessionStorage only costs surviving a reload. */
    }
    setStickyHeader("X-KK-App", on ? "preview" : null);
    set({ on });
  },
}));

export const usePreviewMode = () => usePreviewModeStore((s) => s.on);

// Picked up at load, before the first request goes out.
if (readFlag()) setStickyHeader("X-KK-App", "preview");

/** The Plan tells the phone view something changed. Only from this app's own origin. */
window.addEventListener("message", (e: MessageEvent<unknown>) => {
  if (e.origin !== window.location.origin) return;
  const data = e.data as { type?: unknown } | null;
  if (data && typeof data === "object" && data.type === "kk:refresh" && usePreviewModeStore.getState().on) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
  }
});
